import React from 'react';
import { useFeatureFlag } from '../../../hooks/useFeatureFlags';

export const HolydaysHeaderSnowflakesLayer: React.FC = () => {
  const { isEnabled } = useFeatureFlag('header_snowflakes');

  if (!isEnabled) return null;

  return (
    <div className="header-snowflakes" data-testid="header-snowflakes" aria-hidden="true">
      <svg
        className="header-snowflakes__svg"
        width="100%"
        height="100%"
        viewBox="0 0 1000 80"
        preserveAspectRatio="xMidYMid slice"
        focusable="false"
      >
        <defs>
          <symbol id="plng-snowflake-1" viewBox="0 0 24 24">
            <g
              stroke="rgba(255,255,255,0.95)"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
              transform="translate(12 12)"
            >
              {[0, 60, 120, 180, 240, 300].map((deg) => (
                <g key={deg} transform={`rotate(${deg})`}>
                  <line x1="0" y1="0" x2="10" y2="0" />
                  <line x1="6" y1="0" x2="8" y2="-2" />
                  <line x1="6" y1="0" x2="8" y2="2" />
                </g>
              ))}
              <circle cx="0" cy="0" r="1.4" fill="rgba(255,255,255,0.8)" stroke="none" />
            </g>
          </symbol>

          <symbol id="plng-snowflake-2" viewBox="0 0 24 24">
            <g
              stroke="rgba(255,255,255,0.95)"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
              transform="translate(12 12)"
            >
              {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
                <g key={deg} transform={`rotate(${deg})`}>
                  <line x1="0" y1="0" x2="9" y2="0" />
                  <line x1="5.5" y1="0" x2="7.2" y2="-1.8" />
                  <line x1="5.5" y1="0" x2="7.2" y2="1.8" />
                </g>
              ))}
              <circle cx="0" cy="0" r="1.2" fill="rgba(255,255,255,0.75)" stroke="none" />
            </g>
          </symbol>

          <symbol id="plng-snowflake-3" viewBox="0 0 24 24">
            <g
              stroke="rgba(255,255,255,0.95)"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
              transform="translate(12 12)"
            >
              {[0, 60, 120, 180, 240, 300].map((deg) => (
                <g key={deg} transform={`rotate(${deg})`}>
                  <line x1="0" y1="0" x2="9.2" y2="0" />
                  <line x1="5.2" y1="0" x2="7.0" y2="-2.0" />
                  <line x1="5.2" y1="0" x2="7.0" y2="2.0" />
                  <line x1="7.8" y1="0" x2="8.6" y2="-1.2" />
                  <line x1="7.8" y1="0" x2="8.6" y2="1.2" />
                </g>
              ))}
              <circle cx="0" cy="0" r="1.2" fill="rgba(255,255,255,0.7)" stroke="none" />
            </g>
          </symbol>

          <symbol id="plng-snowflake-4" viewBox="0 0 24 24">
            <g
              stroke="rgba(255,255,255,0.95)"
              strokeWidth="1.25"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
              transform="translate(12 12)"
            >
              {[0, 60, 120, 180, 240, 300].map((deg) => (
                <g key={deg} transform={`rotate(${deg})`}>
                  <line x1="0" y1="0" x2="9.5" y2="0" />
                  <polygon
                    points="6.2,-1.5 7.4,0 6.2,1.5 5,0"
                    fill="rgba(255,255,255,0.35)"
                    stroke="rgba(255,255,255,0.85)"
                    strokeWidth="0.6"
                  />
                </g>
              ))}
              <circle cx="0" cy="0" r="1.1" fill="rgba(255,255,255,0.7)" stroke="none" />
            </g>
          </symbol>

          <symbol id="plng-snowflake-5" viewBox="0 0 24 24">
            <g
              stroke="rgba(255,255,255,0.95)"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
              transform="translate(12 12)"
            >
              {[0, 72, 144, 216, 288].map((deg) => (
                <g key={deg} transform={`rotate(${deg})`}>
                  <line x1="0" y1="0" x2="8.5" y2="0" />
                  <circle cx="7.8" cy="0" r="0.8" fill="rgba(255,255,255,0.55)" stroke="none" />
                </g>
              ))}
              <circle cx="0" cy="0" r="1.0" fill="rgba(255,255,255,0.7)" stroke="none" />
            </g>
          </symbol>

          <pattern
            id="plng-header-snowflakes-pattern"
            width="160"
            height="80"
            patternUnits="userSpaceOnUse"
          >
            <use href="#plng-snowflake-1" x="10" y="10" width="30" height="30" opacity="0.55" />
            <use href="#plng-snowflake-2" x="58" y="8" width="24" height="24" opacity="0.48" />
            <use href="#plng-snowflake-1" x="104" y="14" width="18" height="18" opacity="0.38" transform="rotate(18 113 23)" />

            <use href="#plng-snowflake-2" x="22" y="44" width="20" height="20" opacity="0.44" transform="rotate(-12 32 54)" />
            <use href="#plng-snowflake-1" x="72" y="42" width="26" height="26" opacity="0.50" transform="rotate(10 85 55)" />
            <use href="#plng-snowflake-2" x="118" y="50" width="16" height="16" opacity="0.36" transform="rotate(22 126 58)" />

            <g opacity="0.22">
              <use href="#plng-snowflake-1" x="46" y="28" width="12" height="12" transform="rotate(-20 52 34)" />
              <use href="#plng-snowflake-2" x="92" y="30" width="12" height="12" transform="rotate(30 98 36)" />
              <use href="#plng-snowflake-1" x="138" y="26" width="10" height="10" transform="rotate(8 143 31)" />
            </g>
          </pattern>
        </defs>

        <rect x="0" y="0" width="100%" height="100%" fill="url(#plng-header-snowflakes-pattern)" />
      </svg>
    </div>
  );
};


